import { convertToWebP, getTemplateImageSrc } from "./imageUtils";

export type TemplateDeliveryBudget = {
  /** Cap for about / banner LCP-ish assets. */
  lcpWidth: number;
  avatarWidth: number;
  fcWidth: number;
  /** Product / content grid tiles (multi-column). */
  shopWidth: number;
  quality: number;
};

/**
 * Shrink delivery sizes on constrained networks (3G / Slow 4G).
 * Uses Network Information API when available (Chrome DevTools throttle updates it).
 */
export function getTemplateDeliveryBudget(): TemplateDeliveryBudget {
  try {
    const nav = navigator as Navigator & {
      connection?: {
        effectiveType?: string;
        downlink?: number;
        saveData?: boolean;
      };
    };
    const c = nav.connection;
    const type = String(c?.effectiveType || "").toLowerCase();
    const downlink =
      typeof c?.downlink === "number" && c.downlink > 0 ? c.downlink : null;
    const saveData = Boolean(c?.saveData);

    if (saveData || type === "slow-2g" || type === "2g") {
      return {
        lcpWidth: 420,
        avatarWidth: 320,
        fcWidth: 220,
        shopWidth: 280,
        quality: 58,
      };
    }
    // Slow 3G ~0.4 Mbps; treat low downlink as 3G even if effectiveType stays "3g"/"4g".
    if (type === "3g" || (downlink != null && downlink <= 0.7)) {
      return {
        lcpWidth: 520,
        avatarWidth: 400,
        fcWidth: 260,
        shopWidth: 320,
        quality: 62,
      };
    }
    // Slow 4G-ish (~1–1.5 Mbps downlink in DevTools).
    if (downlink != null && downlink <= 1.6) {
      return {
        lcpWidth: 640,
        avatarWidth: 520,
        fcWidth: 320,
        shopWidth: 360,
        quality: 68,
      };
    }
  } catch {
    /* ignore */
  }
  return {
    lcpWidth: 720,
    avatarWidth: 640,
    fcWidth: 360,
    shopWidth: 420,
    quality: 70,
  };
}

/** LCP / about / banner / shop grid delivery URL under the current network budget. */
export function getBudgetedTemplateImageSrc(
  url: string,
  role: "lcp" | "avatar" | "fc" | "banner" | "shop" = "lcp",
): string {
  const b = getTemplateDeliveryBudget();
  const width =
    role === "avatar"
      ? b.avatarWidth
      : role === "fc"
        ? b.fcWidth
        : role === "shop"
          ? b.shopWidth
          : role === "banner"
            ? Math.min(960, b.lcpWidth + 120)
            : b.lcpWidth;
  // FC tiles are CSS 9:16 cover — request that box explicitly.
  // Other roles use width + resize=contain (see convertToWebP) so aspect holds.
  if (role === "fc") {
    const height = Math.round((width * 16) / 9);
    return convertToWebP(url, width, height, b.quality, "cover");
  }
  if (role === "avatar") {
    // Keep full aspect (width + contain). Server-side square `cover` baked a
    // center crop into the bitmap, so Hero Crop & Zoom (object-position /
    // scale) could not reveal edges the inspector crop still showed.
    // Client `.mte-tpl-bio-ava-frame > img` already uses object-fit: cover.
    return convertToWebP(url, width, undefined, b.quality, "contain");
  }
  if (role === "shop") {
    return convertToWebP(url, width, undefined, b.quality, "contain");
  }
  return getTemplateImageSrc(url, width, b.quality);
}

const preloadedHrefs = new Set<string>();

/**
 * Warm the next hidden catalog chunk with the same shop WebP the cards will
 * request, so the 1.1s scroll loader is decode time, not a cold download.
 */
export function preloadBudgetedShopImages(
  urls: Array<string | null | undefined>,
): void {
  if (typeof window === "undefined") return;
  let started = 0;
  for (const raw of urls) {
    const href = getBudgetedTemplateImageSrc(String(raw || "").trim(), "shop");
    if (!href || preloadedHrefs.has(href)) continue;
    preloadedHrefs.add(href);
    const img = new Image();
    img.decoding = "async";
    img.src = href;
    started += 1;
    if (started >= 25) break;
  }
}

/** Warm URLs that cards will request as-is (already budgeted or a CDN original). */
export function preloadResolvedImageSrcs(
  hrefs: Array<string | null | undefined>,
  limit = 80,
): void {
  if (typeof window === "undefined") return;
  let started = 0;
  for (const raw of hrefs) {
    const href = String(raw || "").trim();
    if (!href || preloadedHrefs.has(href)) continue;
    preloadedHrefs.add(href);
    const img = new Image();
    img.decoding = "async";
    img.src = href;
    started += 1;
    if (started >= limit) break;
  }
}

/**
 * Inject `<link rel="preload" as="image">` for the likely LCP asset as soon as
 * bootstrap knows the URL — starts download during JS parse on Slow/3G.
 */
export function preloadTemplateLcpImage(rawUrl: string | null | undefined): void {
  if (typeof document === "undefined") return;
  const raw = String(rawUrl || "").trim();
  if (!raw || raw.startsWith("blob:") || raw.startsWith("data:")) return;
  const href = getBudgetedTemplateImageSrc(raw, "lcp");
  if (!href || preloadedHrefs.has(href)) return;
  preloadedHrefs.add(href);
  const link = document.createElement("link");
  link.rel = "preload";
  link.as = "image";
  link.href = href;
  // Hint decode for LCP candidates.
  try {
    (link as HTMLLinkElement & { fetchPriority?: string }).fetchPriority = "high";
  } catch {
    /* ignore */
  }
  document.head.appendChild(link);
}

/** Simple in-flight gate so non-LCP images do not stampede on 3G. */
const MAX_CONCURRENT_IMAGES = 2;
let inFlight = 0;
const waitQueue: Array<() => void> = [];

export function acquireImageSlot(): Promise<void> {
  if (inFlight < MAX_CONCURRENT_IMAGES) {
    inFlight += 1;
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    waitQueue.push(() => {
      inFlight += 1;
      resolve();
    });
  });
}

export function releaseImageSlot(): void {
  inFlight = Math.max(0, inFlight - 1);
  const next = waitQueue.shift();
  if (next) next();
}

/** Convert without budget (tests / already-sized). Re-export for convenience. */
export { convertToWebP };
